// qchat_fe/src/Component/SideData/Overview/AiAgentWorkFlow/ConnectorDefinitionModal.jsx

import React, { useState, useEffect, useMemo } from "react";
import {
  FaTimes,
  FaChevronLeft,
  FaExclamationTriangle,
  FaInfoCircle,
  FaSearch,
  FaSync,
} from "react-icons/fa";
import styles from "./PostTriggerModal.module.css";

function humanize(name) {
  if (!name) return "";
  return name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function valueToInput(value, type) {
  if (value === undefined || value === null) return "";
  if (type === "array<string>") {
    if (Array.isArray(value)) return value.join(", ");
    return String(value);
  }
  if (type === "boolean") {
    if (typeof value === "boolean") return value ? "true" : "false";
    return String(value);
  }
  return String(value);
}

function inputToValue(input, type) {
  if (type === "array<string>") {
    return String(input)
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  if (type === "boolean") {
    const s = String(input).trim().toLowerCase();
    return s === "true" || s === "1" || s === "yes";
  }
  if (type === "number") {
    const n = Number(input);
    return Number.isFinite(n) ? n : input;
  }
  return input;
}

const ConnectorDefinitionModal = ({
  isOpen,
  onClose,
  onSave,
  operations,
  loadingOperations,
  editingDefinition,
  connectedProviders = [],
  availableFields = [],
  region, // ⭐ Needed for the Sheets discovery API
  appName, // ⭐ Needed for the Sheets discovery API
}) => {
  const [stage, setStage] = useState("pick");
  const [filter, setFilter] = useState("");

  const [name, setName] = useState("");
  const [selectedOperationName, setSelectedOperationName] = useState("");
  const [paramValues, setParamValues] = useState({});
  const [error, setError] = useState("");

  // ⭐ State for dynamic sheet tabs
  const [sheetTabs, setSheetTabs] = useState([]);
  const [loadingTabs, setLoadingTabs] = useState(false);
  const [tabError, setTabError] = useState("");

  useEffect(() => {
    if (!isOpen) return;

    setError("");
    setFilter("");
    setTabError("");

    if (editingDefinition) {
      setName(editingDefinition.name || "");
      setSelectedOperationName(editingDefinition.operation_name || "");
      setStage("params");

      const op = operations.find(
        (o) => o.name === editingDefinition.operation_name,
      );
      const schema = op?.input_schema || {};
      const initial = {};
      Object.entries(schema).forEach(([fieldName, spec]) => {
        const existing = editingDefinition.operation_params?.[fieldName];
        const fallback = spec.default;
        const value = existing !== undefined ? existing : fallback;
        initial[fieldName] = valueToInput(value, spec.type);
      });
      setParamValues(initial);
    } else {
      setName("");
      setSelectedOperationName("");
      setStage("pick");
      setParamValues({});
      setSheetTabs([]);
    }
  }, [isOpen, editingDefinition, operations]);

  // ⭐ Fetch tabs when spreadsheet_id changes (only for google.sheets.* ops)
  useEffect(() => {
    if (!selectedOperationName?.startsWith("google.sheets.")) {
      setSheetTabs([]);
      return;
    }

    const spreadsheetId = paramValues["spreadsheet_id"];
    if (!spreadsheetId || spreadsheetId.includes("{{")) {
      setSheetTabs([]);
      setTabError("");
      return;
    }

    let cancelled = false;
    setLoadingTabs(true);
    setTabError("");

    (async () => {
      try {
        const token = localStorage.getItem("token");
        const baseUrl = `https://${region}.connector-handler2.${process.env.REACT_APP_SL_BE_BASE_ENV_PART}/${process.env.REACT_APP_STAGE_ENV}`;

        // The connector Lambda exposes a fast, synchronous GET endpoint for
        // this discovery call. See `lambda_handler.py`'s `/sheets/list_tabs`
        // route.
        const url = `${baseUrl}/sheets/list_tabs?spreadsheet_id=${encodeURIComponent(spreadsheetId)}&app_name=${encodeURIComponent(appName || "")}`;

        const resp = await fetch(url, {
          headers: { Authorization: token },
        });

        if (resp.ok) {
          const data = await resp.json();
          if (!cancelled) {
            setSheetTabs(data.tabs || []);
          }
        } else {
          if (!cancelled) {
            const err = await resp.json().catch(() => ({}));
            setTabError(
              err.error || "Failed to load tabs. Check the Spreadsheet ID.",
            );
            setSheetTabs([]);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setTabError("Network error fetching tabs.");
          setSheetTabs([]);
        }
      } finally {
        if (!cancelled) setLoadingTabs(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [paramValues["spreadsheet_id"], selectedOperationName, region, appName]);

  const selectedOperation = useMemo(
    () => operations.find((o) => o.name === selectedOperationName) || null,
    [operations, selectedOperationName],
  );

  const groupedOperations = useMemo(() => {
    const groups = {};
    const filtered = operations.filter((op) => {
      if (!filter) return true;
      const hay =
        `${op.name} ${op.display_name || ""} ${op.description || ""}`.toLowerCase();
      return hay.includes(filter.toLowerCase());
    });
    filtered.forEach((op) => {
      const p = op.provider || "other";
      if (!groups[p]) groups[p] = [];
      groups[p].push(op);
    });
    return groups;
  }, [operations, filter]);

  const handlePickOperation = (op) => {
    setSelectedOperationName(op.name);
    setError("");
    setTabError("");
    if (!name) setName(op.display_name || op.name);

    const schema = op.input_schema || {};
    const initial = {};
    Object.entries(schema).forEach(([fieldName, spec]) => {
      initial[fieldName] = valueToInput(spec.default, spec.type);
    });
    setParamValues(initial);
    setStage("params");
  };

  const handleBack = () => {
    setStage("pick");
    setError("");
  };

  const handleChangeParam = (fieldName, value) => {
    setParamValues((prev) => ({ ...prev, [fieldName]: value }));
  };

  const handleSave = () => {
    if (!selectedOperation) {
      setError("No operation selected.");
      return;
    }
    if (!name.trim()) {
      setError("Give the definition a name.");
      return;
    }

    const schema = selectedOperation.input_schema || {};
    const resolved = {};

    for (const [fieldName, spec] of Object.entries(schema)) {
      const raw = paramValues[fieldName];
      const isRequired = !!spec.required;

      if (isRequired && (raw === undefined || raw === "")) {
        setError(`"${spec.label || humanize(fieldName)}" is required.`);
        return;
      }

      if (raw === undefined || raw === "") continue;

      resolved[fieldName] = inputToValue(raw, spec.type);
    }

    onSave({
      name: name.trim(),
      operation_name: selectedOperation.name,
      operation_params: resolved,
    });
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            {stage === "params" && !editingDefinition && (
              <button
                type="button"
                className={styles.backBtn}
                onClick={handleBack}
                title="Back to operation picker"
              >
                <FaChevronLeft size={12} />
              </button>
            )}
            <h3 className={styles.title}>
              {editingDefinition
                ? "Edit connector action"
                : "New connector action"}
            </h3>
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close"
          >
            <FaTimes size={14} />
          </button>
        </div>

        <div className={styles.body}>
          {stage === "pick" && (
            <>
              <div className={styles.searchRow}>
                <FaSearch size={12} className={styles.searchIcon} />
                <input
                  type="text"
                  className={styles.searchInput}
                  placeholder="Filter operations..."
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                />
              </div>

              {loadingOperations && (
                <div className={styles.loadingBlock}>Loading operations…</div>
              )}

              {!loadingOperations &&
                Object.keys(groupedOperations).length === 0 && (
                  <div className={styles.emptyBlock}>
                    No operations match. Try a different filter.
                  </div>
                )}

              {!loadingOperations &&
                Object.keys(groupedOperations).map((provider) => {
                  const isConnected = connectedProviders.includes(provider);
                  return (
                    <div key={provider} className={styles.providerGroup}>
                      <div className={styles.providerHeader}>
                        <span className={styles.providerHeaderName}>
                          {provider}
                        </span>
                        {!isConnected && (
                          <span className={styles.providerHeaderWarn}>
                            Not connected — connect {provider} first to use
                            these actions
                          </span>
                        )}
                      </div>
                      <div className={styles.opGrid}>
                        {groupedOperations[provider].map((op) => (
                          <button
                            key={op.name}
                            type="button"
                            className={`${styles.opCard} ${
                              !isConnected ? styles.opCardDisabled : ""
                            }`}
                            onClick={() => handlePickOperation(op)}
                            title={
                              isConnected ? "" : `Connect ${provider} first`
                            }
                          >
                            <div className={styles.opCardName}>
                              {op.display_name || op.name}
                            </div>
                            <div className={styles.opCardKey}>
                              <code>{op.name}</code>
                            </div>
                            {op.description && (
                              <div className={styles.opCardDesc}>
                                {op.description}
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
            </>
          )}

          {stage === "params" && selectedOperation && (
            <>
              <div className={styles.selectedOpBar}>
                <div className={styles.selectedOpName}>
                  {selectedOperation.display_name || selectedOperation.name}
                </div>
                <code className={styles.selectedOpKey}>
                  {selectedOperation.name}
                </code>
              </div>

              <div className={styles.fieldGrid}>
                <div className={styles.fieldRow}>
                  <label className={styles.fieldLabelRow}>
                    <span className={styles.fieldLabel}>
                      Definition name
                      <span className={styles.requiredMark}>*</span>
                    </span>
                  </label>
                  <input
                    type="text"
                    className={styles.input}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Demo booking calendar event"
                  />
                </div>
              </div>

              {Object.entries(selectedOperation.input_schema || {}).length ===
                0 && (
                <div className={styles.emptyBlock}>
                  This operation takes no parameters. It's ready to save.
                </div>
              )}

              {Object.entries(selectedOperation.input_schema || {}).length >
                0 && (
                <div className={styles.fieldGrid}>
                  {Object.entries(selectedOperation.input_schema || {}).map(
                    ([fieldName, spec]) => {
                      // ⭐ Special handling for Google Sheets
                      const isSheetOp =
                        selectedOperation.name.startsWith("google.sheets.");
                      const isSpreadsheetId = fieldName === "spreadsheet_id";
                      const isSheetRange = fieldName === "sheet_range";

                      if (isSheetOp && isSheetRange && sheetTabs.length > 0) {
                        const currentValue = paramValues[fieldName] || "";
                        const parts = currentValue.split("!");
                        const currentTab = parts[0] || "";
                        const currentRange = parts[1] || "A:Z";

                        return (
                          <div key={fieldName} className={styles.fieldRow}>
                            <label className={styles.fieldLabelRow}>
                              <span className={styles.fieldLabel}>
                                {spec.label || humanize(fieldName)}
                                {spec.required && (
                                  <span className={styles.requiredMark}>*</span>
                                )}
                              </span>
                            </label>
                            <div style={{ display: "flex", gap: 8 }}>
                              <select
                                className={styles.input}
                                value={currentTab}
                                onChange={(e) =>
                                  handleChangeParam(
                                    fieldName,
                                    `${e.target.value}!${currentRange}`,
                                  )
                                }
                                style={{ flex: 1 }}
                              >
                                <option value="">-- Select a Tab --</option>
                                {sheetTabs.map((tab) => (
                                  <option key={tab} value={tab}>
                                    {tab}
                                  </option>
                                ))}
                              </select>
                              <input
                                type="text"
                                className={styles.input}
                                value={currentRange}
                                onChange={(e) =>
                                  handleChangeParam(
                                    fieldName,
                                    `${currentTab}!${e.target.value}`,
                                  )
                                }
                                placeholder="A:Z"
                                style={{ width: 100 }}
                                title="Column range (e.g. A:Z)"
                              />
                            </div>
                          </div>
                        );
                      }

                      if (isSheetOp && isSpreadsheetId) {
                        return (
                          <div key={fieldName} className={styles.fieldRow}>
                            <label className={styles.fieldLabelRow}>
                              <span className={styles.fieldLabel}>
                                {spec.label || humanize(fieldName)}
                                {spec.required && (
                                  <span className={styles.requiredMark}>*</span>
                                )}
                                {loadingTabs && (
                                  <FaSync
                                    className={styles.spin}
                                    size={10}
                                    style={{ marginLeft: 6, color: "#718096" }}
                                  />
                                )}
                              </span>
                            </label>
                            <input
                              type="text"
                              className={styles.input}
                              value={paramValues[fieldName] || ""}
                              onChange={(e) =>
                                handleChangeParam(fieldName, e.target.value)
                              }
                              placeholder="Paste Spreadsheet ID here..."
                            />
                            {tabError && (
                              <div
                                style={{
                                  fontSize: 11,
                                  color: "#dc2626",
                                  marginTop: 4,
                                }}
                              >
                                {tabError}
                              </div>
                            )}
                            {!tabError && (
                              <div
                                style={{
                                  fontSize: 10,
                                  color: "#718096",
                                  marginTop: 4,
                                  lineHeight: 1.5,
                                }}
                              >
                                Find this in your Google Sheet URL:
                                <br />
                                <code>
                                  docs.google.com/spreadsheets/d/
                                  <strong>[ID]</strong>/edit
                                </code>
                              </div>
                            )}
                          </div>
                        );
                      }

                      // Default fallback
                      return (
                        <DefinitionFieldRow
                          key={fieldName}
                          fieldName={fieldName}
                          spec={spec}
                          value={paramValues[fieldName] ?? ""}
                          onChange={(v) => handleChangeParam(fieldName, v)}
                          availableFields={availableFields}
                        />
                      );
                    },
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className={styles.footer}>
          {error && (
            <div className={styles.footerError}>
              <FaExclamationTriangle size={12} />
              <span>{error}</span>
            </div>
          )}
          <div className={styles.footerActions}>
            <button
              type="button"
              className={styles.cancelBtn}
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.saveBtn}
              onClick={handleSave}
              disabled={stage !== "params" || !selectedOperation}
            >
              {editingDefinition ? "Save changes" : "Save action"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

function DefinitionFieldRow({
  fieldName,
  spec,
  value,
  onChange,
  availableFields,
}) {
  const label = spec.label || humanize(fieldName);
  const help = spec.help || "";
  const isRequired = !!spec.required;
  const type = spec.type;

  return (
    <div className={styles.fieldRow}>
      <label className={styles.fieldLabelRow}>
        <span className={styles.fieldLabel}>
          {label}
          {isRequired && <span className={styles.requiredMark}>*</span>}
        </span>
        {help && (
          <span className={styles.helpIcon} title={help}>
            <FaInfoCircle size={11} />
          </span>
        )}
      </label>

      {type === "boolean" ? (
        <div className={styles.booleanRow}>
          <select
            className={styles.input}
            value={value || "false"}
            onChange={(e) => onChange(e.target.value)}
          >
            <option value="false">No</option>
            <option value="true">Yes</option>
          </select>
        </div>
      ) : type === "array<string>" ? (
        <input
          type="text"
          className={styles.input}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Comma-separated, e.g. {{ email }}, sales@team.com"
        />
      ) : type === "number" ? (
        <input
          type="number"
          className={styles.input}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={spec.default !== undefined ? String(spec.default) : ""}
        />
      ) : (
        <input
          type="text"
          className={styles.input}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={
            spec.default !== undefined
              ? String(spec.default)
              : "Enter a value or {{ field_name }}"
          }
        />
      )}

      {availableFields.length > 0 && (
        <div className={styles.fieldChips}>
          <span className={styles.fieldChipsLabel}>Available fields:</span>
          {availableFields.slice(0, 8).map((f) => (
            <button
              key={f}
              type="button"
              className={styles.fieldChip}
              onClick={() => onChange((value || "") + `{{ ${f} }}`)}
              title={`Insert {{ ${f} }}`}
            >
              {f}
            </button>
          ))}
          {availableFields.length > 8 && (
            <span className={styles.fieldChipsMore}>
              +{availableFields.length - 8} more
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default ConnectorDefinitionModal;